/* eslint-disable react/destructuring-assignment */

import React, { Component } from 'react';
import { faEye, faListOl, faTimes, faVideo } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment-timezone';
import { Link } from 'react-router-dom';
import { Filter } from 'react-table';
import { EventState, EventType } from 'stageclip-common';
import { isEqual } from 'lodash';

import { EventsTableColumns } from '../../events/eventsTable';

import { callApi } from 'helpers/callApi/callApi';
import { FiltersFormContainer } from 'components/eventFilters/EventFilters';
import * as DateHelpers from 'helpers/dateHelpers';
import * as PermissionsHelper from 'helpers/permissionsHelper';
import * as UserHelper from 'helpers/userHelper';
import { NormalizedEvent } from 'models/normalized/normalizedEvent';
import Button from 'components/buttons/button';
import { ClientUserTimezonesDate } from 'components/date/clientUserTimezonesDate';
import { DateTimeRangeSelector } from 'components/date/dateTimeRangeSelector';
import { AppTable, AppTableColumn } from 'components/table/appTable';
import Icon from 'components/utils/icon';
import styles from './MonitoringTable.module.scss';
import { EventsTableOwnProps, EventsTableState } from './MonitoringTable.types';

const fixedColumns = [EventsTableColumns.Actions];

const defaultFiltered = [
  {
    id: EventsTableColumns.EventStart,
    value: {
      endDate: moment().add(14, 'days'),
      startDate: moment().subtract(14, 'days'),
    },
  },
  {
    id: EventsTableColumns.EventType,
    value: EventType.VirtualGraduation,
  },
];

export class MonitoringTable extends Component<EventsTableOwnProps, EventsTableState> {
  protected tableRef = React.createRef<any>();

  private static renderState(value: any, className: string) {
    return (
      <>
        <span className={className}>{value}</span>
      </>
    );
  }

  private static getStateCell(row: any) {
    switch (row.value) {
      case EventState.WaitingForAssets:
        return MonitoringTable.renderState(
          <>
            Waiting&nbsp;&nbsp;
            {row.original.totalNoOfParticipants === 0 && <Icon icon={faListOl} noMargins transparent />}
            {row.original.totalNoOfEventVideos === 0 && <Icon icon={faVideo} noMargins transparent />}
          </>,
          styles.waitingAssetsState,
        );
      case EventState.ReadyToClip:
        return MonitoringTable.renderState('Ready', styles.readyToClipState);
      case EventState.ClippingInProgress:
        return MonitoringTable.renderState('Clipping', styles.inProgressState);
      case EventState.ReviewNeeded:
        return MonitoringTable.renderState('Review', styles.inReviewState);
      case EventState.Complete:
        return MonitoringTable.renderState('Complete', styles.completeState);
      case EventState.Cancelled:
        return MonitoringTable.renderState('Cancelled', styles.cancelledState);
      default:
        return MonitoringTable.renderState(row.value, styles.state);
    }
  }

  timer: any;

  columns: AppTableColumn[] = [
    {
      Header: 'Event Data',
      columns: [
        {
          Header: 'Client',
          accessor: (event: NormalizedEvent) => event.client.name,
          className: 'break-words',
          dropdownFilter: true,
          id: EventsTableColumns.ClientName,
          width: 290,
        },
        {
          Cell: (row: any) => <Link to={`/events/${row.original.id}`}>{row.value || 'No Name (View)'}</Link>,
          Header: 'Event Name',
          accessor: (event: NormalizedEvent) => event.name,
          className: 'emphasize break-words',
          dropdownFilter: true,
          id: EventsTableColumns.EventName,
          width: 250,
        },
        {
          Header: 'Event Type',
          accessor: (event: NormalizedEvent) => event.type,
          className: 'break-words',
          dropdownFilter: true,
          id: EventsTableColumns.EventType,
          width: 120,
        },
        {
          Cell: (row: any) => (
            <ClientUserTimezonesDate
              date={row.value}
              timezone={row.original.timezone || row.original.client.timezone}
            />
          ),
          Filter: ({ onChange, filter: { value } }: any) => (
            <DateTimeRangeSelector startDate={value.startDate} endDate={value.endDate} onChange={onChange} />
          ),
          Header: 'Event Start',
          accessor: (event: NormalizedEvent) => DateHelpers.sortableDateFormat(event.startTime),
          filterMethod: (filter: Filter, row: any) => {
            const eventStart = moment(row.eventStart).tz(moment.tz.guess());

            return (
              (!filter.value.startDate || eventStart.isSameOrAfter(filter.value.startDate)) &&
              (!filter.value.endDate || eventStart.isSameOrBefore(filter.value.endDate))
            );
          },
          filterable: true,
          id: EventsTableColumns.EventStart,
          width: 230,
        },
        {
          Header: 'Assigned To',
          accessor: (event: NormalizedEvent) =>
            event.assignee ? UserHelper.userFullName(this.props.users[event.assignee]) : 'Unassigned',
          className: 'break-words',
          dropdownFilter: true,
          id: EventsTableColumns.Assignee,
          width: 100,
        },
        {
          Cell: MonitoringTable.getStateCell,
          Header: 'Status',
          accessor: (event: NormalizedEvent) => event.state,
          dropdownFilter: true,
          id: EventsTableColumns.Status,
          width: 150,
        },
      ],
    },
    {
      Cell: (row: any) => (
        <Link to={`/events/${row.original.id}`}>
          <Button text="View" icon={faEye} fill />
        </Link>
      ),
      Header: 'Actions',
      accessor: () => null,
      className: styles.actions,
      id: 'actions',
      width: 70,
    },
  ];

  idsOfVisibleEvents: string[] = [];

  constructor(props: any) {
    super(props);
    this.state = {
      lastLoadedStats: undefined,
      events: [],
      loading: false,
    };
    this.displayEventStats();
    this.resetFilter = this.resetFilter.bind(this);
  }

  componentDidMount() {
    this.getEvents({ active: true, archived: false });

    if (this.props.getStatistics) {
      this.getStatistics();
      this.timer = setInterval(this.getStatistics.bind(this), 30000);
    }
  }

  componentWillUnmount() {
    clearInterval(this.timer);
    this.timer = null;
  }

  getEvents(filters?: { active: boolean; archived: boolean }) {
    callApi({
      action: () => {
        this.setState({ loading: true });

        return this.props.fetchEvents(filters);
      },
      onSuccess: ({ payload }) => {
        this.setState({ events: payload, loading: false });
      },
    });
  }

  getStatistics() {
    if (PermissionsHelper.userCan(PermissionsHelper.RestrictedActions.ViewEventStatistics)) {
      callApi({
        action: () => this.props.getStatistics(this.idsOfVisibleEvents),
        onSuccess: ({ payload }) => {
          this.setState(prevState => ({
            events: prevState.events.map((event: any) => {
              return {
                ...event,
                ...payload[event.id],
              };
            }),
            lastLoadedStats: moment(),
          }));
        },
      });
    }
  }

  handleFilterChange = (filteredRows: any[], allRows: any[]) => {
    const prevIdsOfVisibleEvents = [...this.idsOfVisibleEvents];
    // eslint-disable-next-line no-underscore-dangle
    this.idsOfVisibleEvents = filteredRows.map(r => r._original.id);

    if (!isEqual(prevIdsOfVisibleEvents.sort(), this.idsOfVisibleEvents.sort())) {
      this.getStatistics();
    }

    if (this.props.onFilterChange) {
      this.props.onFilterChange(filteredRows, allRows);
    }
  };

  resetFilter = () => {
    if (this.tableRef.current) {
      this.tableRef.current.setFilters(defaultFiltered);
    }
  };

  onFilters = (filters: any) => {
    this.getEvents(filters);
    this.getStatistics();
  };

  hasColumn(column: EventsTableColumns): boolean {
    if (!this.props.columns) {
      return true;
    }

    return this.props.columns.concat(fixedColumns).indexOf(column) >= 0;
  }

  displayEventStats() {
    function getEventStatistics(event: NormalizedEvent, data: any) {
      const statisticsLoaded = data !== undefined;

      return statisticsLoaded ? data || 0 : '-';
    }

    if (PermissionsHelper.userCan(PermissionsHelper.RestrictedActions.ViewEventStatistics)) {
      this.columns.splice(-1, 0, {
        Header: 'Participants Stats',
        columns: [
          {
            Cell: (row: any) => getEventStatistics(row.original, row.value),
            Header: 'Total',
            accessor: (event: NormalizedEvent) => event.noOfParticipants,
            className: 'left-border',
            id: EventsTableColumns.NoOfParticipants,
            width: 60,
          },
          {
            Cell: (row: any) => getEventStatistics(row.original, row.value),
            Header: 'Uploaded',
            accessor: (event: NormalizedEvent) => event.noOfParticipantsWithUploadedData,
            id: EventsTableColumns.NoOfParticipantsWithUploadedData,
            width: 80,
          },
          {
            Cell: (row: any) => getEventStatistics(row.original, row.value),
            Header: 'Submitted',
            accessor: (event: NormalizedEvent) => event.noOfParticipantsWithUploadedDataInSubmittedState,
            id: EventsTableColumns.NoOfParticipantsWithUploadedDataInSubmittedState,
            width: 80,
          },
          {
            Cell: (row: any) => getEventStatistics(row.original, row.value),
            Header: 'Accepted',
            accessor: (event: NormalizedEvent) => event.noOfParticipantsWithUploadedDataInAcceptedState,
            id: EventsTableColumns.NoOfParticipantsWithUploadedDataInAcceptedState,
            width: 70,
          },
          {
            Cell: (row: any) => getEventStatistics(row.original, row.value),
            Header: 'Rejected',
            accessor: (event: NormalizedEvent) => event.noOfParticipantsInRejectedState,
            id: EventsTableColumns.NoOfParticipantsInRejectedState,
            width: 80,
          },
          {
            Cell: (row: any) => getEventStatistics(row.original, row.value),
            Header: 'Total',
            accessor: (event: NormalizedEvent) => event.totalParticipants,
            className: 'left-border',
            id: EventsTableColumns.TotalParticipants,
            width: 60,
          },
          {
            Cell: (row: any) => getEventStatistics(row.original, row.value),
            Header: 'Generated',
            accessor: (event: NormalizedEvent) => event.totalClipsGenerated,
            id: EventsTableColumns.ParticipantsWithClipsGenerated,
            width: 80,
          },
          {
            Cell: (row: any) => getEventStatistics(row.original, row.value),
            Header: 'Processing',
            accessor: (event: NormalizedEvent) => event.totalClipsProcessing,
            id: EventsTableColumns.TotalClipsProcessing,
            width: 80,
          },
          {
            Cell: (row: any) => getEventStatistics(row.original, row.value),
            Header: 'Pending',
            accessor: (event: NormalizedEvent) => event.totalClipsPending,
            id: EventsTableColumns.TotalClipsPending,
            width: 70,
          },
          {
            Cell: (row: any) => getEventStatistics(row.original, row.value),
            Header: 'Waiting data',
            accessor: (event: NormalizedEvent) => event.totalClipsWithoutData,
            id: EventsTableColumns.TotalClipsWaitingForData,
            width: 80,
          },
          {
            Cell: (row: any) => getEventStatistics(row.original, row.value),
            Header: 'Errors',
            accessor: (event: NormalizedEvent) => event.totalClipErrors,
            id: EventsTableColumns.ClipGenerationErrors,
            width: 60,
          },
        ],
      });
    }
  }

  filterColumnsForDisplay(columns: AppTableColumn[] = this.columns): AppTableColumn[] {
    if (!this.props.columns) {
      return columns;
    }

    return columns.reduce((result, c) => {
      if (c.columns) {
        if (c.columns.length === 0) {
          return result;
        }

        return result.concat({ ...c, columns: this.filterColumnsForDisplay(c.columns) });
      }

      return this.hasColumn(c.id as EventsTableColumns) ? result.concat(c) : result;
    }, []);
  }

  renderResetFilterButton(): JSX.Element {
    return (
      <span className={styles.actions}>
        <Button onClick={this.resetFilter} icon={faTimes} inverseColours allCapitals text="Reset Filters" />
      </span>
    );
  }

  render() {
    const rowProps = (state: any, rowInfo: any) => ({
      className: new Date(rowInfo.original.startTime) > new Date() ? styles.futureEventRow : '',
    });

    const noDataProps = () => ({
      className: styles.noData,
    });

    return (
      <div>
        <AppTable
          columns={this.filterColumnsForDisplay()}
          data={this.state.events || []}
          tableName="eventsMonitoring"
          defaultFiltered={defaultFiltered}
          defaultSorted={[{ id: EventsTableColumns.EventStart, desc: false }]}
          tableHeaderButtons={this.renderResetFilterButton()}
          className="with-header-groups"
          noDataText="No data to display - maybe data is filtered out?"
          getNoDataProps={noDataProps}
          defaultPageSize={10}
          onFilterChange={this.handleFilterChange}
          tableHeaderChildren={
            this.props.getStatistics &&
            this.state.lastLoadedStats && (
              <span>Stats last refreshed at {this.state.lastLoadedStats.format('DD-MM-YYYY HH:mm:ss')}</span>
            )
          }
          filters={<FiltersFormContainer onFilters={this.onFilters} />}
          getTrProps={rowProps}
          id="events_monitoring"
          cacheFilters={[EventsTableColumns.EventStart]}
          loading={this.state.loading}
        />
      </div>
    );
  }
}
